import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/components/account/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mismatch = confirm && confirm !== password;

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get("/auth/reset-password/validate", { params: { token } })
      .then((r) => { setValid(r.data.valid); setEmail(r.data.email || ""); })
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) return;
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Your password has been reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (valid === null) {
    return <AuthShell><p className="text-muted-foreground mt-10" data-testid="reset-checking">Checking your link…</p></AuthShell>;
  }
  if (!valid) {
    return (
      <AuthShell>
        <div className="mt-10 space-y-4" data-testid="reset-link-invalid">
          <h2 className="text-lg font-serif font-semibold">This link is no longer valid</h2>
          <p className="text-sm text-muted-foreground">Reset links expire after 30 minutes and can only be used once.</p>
          <Link to="/forgot-password" className="text-sm text-primary hover:underline inline-block" data-testid="request-new-link">Request a new link</Link>
        </div>
      </AuthShell>
    );
  }
  return (
    <AuthShell>
      <p className="text-muted-foreground mt-6">
        Choose a new password for <strong>{email}</strong>. At least 10 characters with an uppercase letter, a lowercase letter and a number.
      </p>
      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-password-form">
        <div className="space-y-2">
          <Label htmlFor="rp-new">New password</Label>
          <Input id="rp-new" type="password" required minLength={10} autoComplete="new-password" value={password}
                 onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="reset-new-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="rp-confirm">Confirm new password</Label>
          <Input id="rp-confirm" type="password" required autoComplete="new-password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="reset-confirm-password-input" />
          {mismatch && <p className="text-xs text-destructive" data-testid="reset-password-mismatch">The two passwords do not match.</p>}
        </div>
        {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
        <Button type="submit" disabled={busy || Boolean(mismatch)} className="w-full h-11 text-base" data-testid="reset-password-submit-btn">
          {busy ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthShell>
  );
}
